// Hooks
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUserQuery, useUserMutation } from '@features/redux/apis/auth';

// Base Components
import { Grid, Paper, Typography, Box, Tooltip } from '@mui/material';
import { ManageForm } from '@components/forms/ManageForm';
import { FormCheckBox } from '@components/forms/FormCheckBox';
import { FormTextArea } from '@components/forms/text/base/FormTextArea';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { FormActions } from '@components/forms/FormActions';

// Fixed Scope Components
import { MultiOrgDropdowns } from '@components/forms/dropdowns/MultiOrgDropdowns';
import { UserEffects } from './UserEffects';
import { ResetPassword } from './ResetPassword';

// Utils, Consts
import { Operation } from '@/types/general';
import { Query } from '@features/redux/apis/utils';
import { AppRoutes } from '@consts/routes';
import { Validate } from '@utils/validate';

// Types
import type { User as UserType } from '@features/redux/apis/auth';
import { store, type RootState } from '@features/redux/store';
import type { Option } from '@components/forms/dropdowns/base/FormMultiSelect';

// Default user state for creating new users
const defaultUserState: UserType = {
    given_name: '',
    family_name: '',
    email: '',
    password: '',
    'custom:PriceEditRole': '0',
    'custom:UploaderRole': '0',
    'custom:UserEditRole': '0',
    'custom:ProductEditRole': '0',
    'custom:StoreEditRole': '0',
    'custom:Notes': '',
    'custom:Companies': '',
    'custom:Locations': '',
    'custom:Resellers': '',
    Username: '',
    Enabled: true,
    UserStatus: '',
    sub: '',
    'custom:userType': '',
};

const useUser = Query.withDraft(useUserQuery, defaultUserState, (fetched, init) => {
    // If fetched data exists, use it; otherwise use the initial state
    return fetched || init || defaultUserState;
});

// Create a wrapper to ensure draft state is never undefined
const useUserWithNonNullableDraft = (args: Parameters<typeof useUser>[0], options?: Parameters<typeof useUser>[1]) => {
    const result = useUser(args, options);
    const [draftState, setDraftState] = result.draft;

    // Ensure draft state is never undefined
    const safeDraftState = draftState || defaultUserState;

    // Create a wrapped setter that ensures we never set undefined
    const safeSetter: React.Dispatch<React.SetStateAction<UserType>> = (action) => {
        if (typeof action === 'function') {
            setDraftState((prev) => action(prev || defaultUserState));
        } else {
            setDraftState(action);
        }
    };

    const safeDraft: [UserType, typeof safeSetter] = [safeDraftState, safeSetter];

    return {
        ...result,
        draft: safeDraft,
    };
};

export const UserPage = () => {
    const navigate = useNavigate();
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const userId = useQuery().get('id');
    const operation = userId ? Operation.Edit : Operation.Create;

    const { data: user, draft, isLoading } = useUserWithNonNullableDraft({ id: userId! }, { skip: !userId });
    const [draftState, setDraftState] = draft;

    const [mutate] = useUserMutation({
        fixedCacheKey: operation === Operation.Edit ? 'UserMutation' : 'UserCreation',
    });

    const selectedState = useState<Record<'companies' | 'resellers' | 'locations', Option[]>>({
        companies: [],
        resellers: [],
        locations: [],
    });

    const [selected] = selectedState;

    return (
        <>
            <UserEffects />

            <ManageForm title={operation === Operation.Create ? 'Create New User' : 'Edit User'} loading={isLoading}>
                <FormRequiredText
                    size={6}
                    label='First Name'
                    draft={draft}
                    stateKey='given_name'
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid first name.',
                    }}
                    validateFn={Validate.name}
                />

                <FormRequiredText
                    size={6}
                    label='Last Name'
                    draft={draft}
                    stateKey='family_name'
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid last name.',
                    }}
                    validateFn={Validate.name}
                />

                <FormRequiredText
                    size={operation === Operation.Edit ? 8 : 6}
                    helperText={{
                        valid: '',
                        invalid: 'Please enter a proper email address',
                    }}
                    label='Email Address'
                    stateKey={'email'}
                    type={'email'}
                    draft={draft}
                    validateFn={Validate.Auth.email}
                    disabled={operation === Operation.Edit}
                />

                {operation === Operation.Edit && <ResetPassword user={user!} />}

                {operation === Operation.Create && (
                    <FormRequiredText
                        size={6}
                        helperText={{
                            valid: '',
                            invalid: 'Please enter a password with 6 characters or more',
                        }}
                        label='Password'
                        draft={draft}
                        stateKey={'password'}
                        type={'password'}
                        disabled={false}
                        validateFn={Validate.Auth.password}
                    />
                )}

                <MultiOrgDropdowns
                    state={selectedState}
                    populate={{
                        companies: user?.['custom:Companies'],
                        resellers: user?.['custom:Resellers'],
                        locations: user?.['custom:Locations'],
                    }}
                />

                <Grid container sx={{ ml: '15px', mt: '20px' }}>
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #cce7ff',
                                borderRadius: 2,
                                borderLeft: '4px solid #6c757d',
                            }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant='subtitle2'
                                    sx={{
                                        color: '#495057',

                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        fontSize: '0.75rem',
                                    }}>
                                    Permissions
                                </Typography>
                                <Typography
                                    variant='caption'
                                    sx={{
                                        color: '#6c757d',

                                        fontSize: '0.6875rem',
                                        display: 'block',
                                        mt: 0.25,
                                    }}>
                                    These permissions control features and data access
                                </Typography>
                            </Box>

                            <Grid container>
                                <Tooltip
                                    title='Grants access to manage user accounts, create new users, edit user permissions, and view user activity'
                                    arrow
                                    placement='top'>
                                    <Box sx={{ display: 'flex', flex: 1 }}>
                                        <FormCheckBox
                                            hidden={!role.users.edit}
                                            label='Manage Users'
                                            draft={draft}
                                            stateKey={'custom:UserEditRole'}
                                            values={{
                                                checked: '1',
                                                unchecked: '0',
                                            }}
                                        />
                                    </Box>
                                </Tooltip>

                                <Tooltip
                                    title='Grants access to manage store information, configure store settings, configure system settings, and view organization data'
                                    arrow
                                    placement='top'>
                                    <Box sx={{ display: 'flex', flex: 1 }}>
                                        <FormCheckBox
                                            hidden={!role.stores.edit}
                                            label='Manage Locations and Systems'
                                            draft={draft}
                                            stateKey={'custom:StoreEditRole'}
                                            values={{
                                                checked: '1',
                                                unchecked: '0',
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                            </Grid>
                        </Paper>
                    </Grid>

                    {role.isSuperAdmin && (
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    backgroundColor: '#f0f8ff',

                                    border: '1px solid #e3e6ea',
                                    borderRadius: 2,
                                    borderLeft: '4px solid #2196f3',
                                }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant='subtitle2'
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1976d2',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            fontSize: '0.75rem',
                                        }}>
                                        Internal Permissions
                                    </Typography>
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            color: '#1976d2',
                                            fontSize: '0.6875rem',
                                            display: 'block',
                                            mt: 0.25,
                                        }}>
                                        These permissions are restricted to internal users only
                                    </Typography>
                                </Box>

                                <Grid container>
                                    <Tooltip
                                        title='Grants access to billing features including invoices, batch processing, and billing changes management'
                                        arrow
                                        placement='top'>
                                        <Box sx={{ display: 'flex', flex: 1 }}>
                                            <FormCheckBox
                                                hidden={!role.prices.edit}
                                                disabled={Object.values(selected).some((item) => item?.[0]?.id !== '*')}
                                                label='Billing Management'
                                                draft={draft}
                                                stateKey={'custom:PriceEditRole'}
                                                values={{
                                                    checked: '1',
                                                    unchecked: '0',
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>

                                    <Tooltip
                                        title='Grants access to design management including uploading models, managing manufacturers, and configuring coverages'
                                        arrow
                                        placement='top'>
                                        <Box sx={{ display: 'flex', flex: 1 }}>
                                            <FormCheckBox
                                                hidden={!role.uploading.edit}
                                                disabled={Object.values(selected).some((item) => item?.[0]?.id !== '*')}
                                                label='Edit Designs'
                                                draft={draft}
                                                stateKey={'custom:UploaderRole'}
                                                values={{
                                                    checked: '1',
                                                    unchecked: '0',
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Grid>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {role.isAdmin && <FormTextArea draft={draft} stateKey='custom:Notes' helperText='Notes' />}

                <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                    <FormActions
                        onSave={() => {
                            const userToken = store.getState().auth.TokenPayload;

                            const companies = selected.companies.map(({ id }) => id).join(',');
                            const locations = selected.locations.map(({ id }) => id).join(',');
                            const resellers = selected.resellers.map(({ id }) => id).join(',');

                            const user: UserType = {
                                ...defaultUserState,
                                ...draftState,
                                Username: draftState.email || '',
                                // Ensure required fields are not undefined
                                given_name: draftState.given_name || '',
                                family_name: draftState.family_name || '',
                                email: draftState.email || '',
                                // ? If "All" was selected, give the user all the companies/locations/resellers
                                // ? the creating user has.
                                // ! This is not the cleanest way to do this, but is the most atomic right now
                                'custom:Companies': companies === '*' ? userToken!['custom:Companies']! : companies,
                                'custom:Locations': locations === '*' ? userToken!['custom:Locations']! : locations,
                                'custom:Resellers': resellers === '*' ? userToken!['custom:Resellers']! : resellers,
                            };

                            mutate({
                                method: operation === Operation.Create ? 'POST' : 'PUT',
                                user,
                            });
                        }}
                        onCancel={() => navigate(AppRoutes.Users)}
                        isUpdate={operation === Operation.Edit}
                        disabled={
                            !Validate.Auth.user(draftState) ||
                            !Object.values(selected).every((arr) => Boolean(arr.length)) ||
                            (operation === Operation.Create && !Validate.Auth.password(draftState.password!))
                        }
                    />
                </Grid>
            </ManageForm>
        </>
    );
};
